// Ad-source attribution: captures UTM/fbclid params on every page load, keeps a
// never-overwritten first_touch and an updating last_touch in localStorage + a
// 90-day first-party cookie, and exposes them to the Matrix + booking flows.
const PARAM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid"];
const STORE_KEY = "sf_attribution";
const COOKIE_DAYS = 90;
const ORGANIC = "direct/organic";

const canUse = () => typeof window !== "undefined" && typeof document !== "undefined";

function readCookie() {
  const m = document.cookie.match(new RegExp(`(?:^|; )${STORE_KEY}=([^;]*)`));
  if (!m) return null;
  try {
    return JSON.parse(decodeURIComponent(m[1]));
  } catch (e) {
    return null;
  }
}

function readStore() {
  if (!canUse()) return null;
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return readCookie();
}

function writeStore(data) {
  const json = JSON.stringify(data);
  try {
    window.localStorage.setItem(STORE_KEY, json);
  } catch (e) {}
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${STORE_KEY}=${encodeURIComponent(json)}; Max-Age=${COOKIE_DAYS * 86400}; Path=/; SameSite=Lax${secure}`;
}

function parseParams(search) {
  const qs = new URLSearchParams(search || "");
  const out = {};
  PARAM_KEYS.forEach((k) => {
    const v = fullyDecode(qs.get(k) || "").trim();
    if (v) out[k] = v.slice(0, 300);
  });
  return out;
}

// Ad platforms sometimes double-encode labels (R1%2520%257C...). Keep decoding
// until no %XX escapes remain so the readable name is what gets saved.
function fullyDecode(value) {
  let v = value;
  for (let i = 0; i < 3 && /%[0-9A-Fa-f]{2}/.test(v); i++) {
    try {
      const next = decodeURIComponent(v.replace(/\+/g, " "));
      if (next === v) break;
      v = next;
    } catch (e) {
      break;
    }
  }
  return v;
}

function buildTouch(params) {
  const isAd = Object.keys(params).length > 0;
  return {
    source: isAd ? params.utm_source || "ad" : ORGANIC,
    utm_source: params.utm_source || "",
    utm_medium: params.utm_medium || "",
    utm_campaign: params.utm_campaign || "",
    utm_term: params.utm_term || "",
    utm_content: params.utm_content || "",
    fbclid: params.fbclid || "",
    referrer: (document.referrer || "").slice(0, 500),
    landing_page: window.location.href.slice(0, 600),
    timestamp: new Date().toISOString(),
  };
}

// Call on every page load / route change. Ad params always win: they create a
// new last_touch, and they replace a first_touch that was only "direct/organic".
export function captureAttribution() {
  if (!canUse()) return;
  const params = parseParams(window.location.search);
  const data = readStore() || {};
  if (Object.keys(params).length > 0) {
    if (sameAd(params, data.last_touch)) {
      // Same ad values carried on an internal link (e.g. /watch -> /matrix): same visit, not a new touch.
      if (!data.first_touch || data.first_touch.source === ORGANIC) data.first_touch = data.last_touch;
      writeStore(data);
      return;
    }
    const touch = buildTouch(params);
    if (!data.first_touch || data.first_touch.source === ORGANIC) data.first_touch = touch;
    data.last_touch = touch;
    writeStore(data);
  } else if (!data.first_touch) {
    const touch = buildTouch({});
    writeStore({ first_touch: touch, last_touch: touch });
  } else if (!readCookie()) {
    writeStore(data); // keep the cookie copy alive
  }
}

function sameAd(params, touch) {
  if (!touch || touch.source === ORGANIC) return false;
  return PARAM_KEYS.every((k) => (params[k] || "") === (touch[k] || ""));
}

// Query string of the saved (last-touch) ad values, for carrying onto internal CTA links.
export function utmQueryString() {
  const { last_touch } = getAttribution();
  if (!last_touch || last_touch.source === ORGANIC) return "";
  const qs = new URLSearchParams();
  PARAM_KEYS.forEach((k) => {
    if (last_touch[k]) qs.set(k, last_touch[k]);
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export function getAttribution() {
  const data = readStore() || {};
  return { first_touch: data.first_touch || null, last_touch: data.last_touch || null };
}

// Appends the last-touch UTM values to a Calendly scheduling URL so Calendly
// stores them on the invitee (its native UTM tracking fields).
export function withCalendlyTracking(href) {
  if (!href) return href;
  const { last_touch } = getAttribution();
  const utm = {
    utm_source: (last_touch && (last_touch.utm_source || last_touch.source)) || ORGANIC,
    utm_medium: last_touch ? last_touch.utm_medium : "",
    utm_campaign: last_touch ? last_touch.utm_campaign : "",
    utm_term: last_touch ? last_touch.utm_term : "",
    utm_content: last_touch ? last_touch.utm_content : "",
  };
  // Append by hand: URL.searchParams would re-encode existing spaces as "+", which Calendly shows literally.
  const extra = Object.entries(utm)
    .filter(([k, v]) => v && !new RegExp(`[?&]${k}=`).test(href))
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
  if (!extra.length) return href;
  return `${href}${href.includes("?") ? "&" : "?"}${extra.join("&")}`;
}

// Who/where a booking was started from, so the Calendly completion event can
// be tied back to the visitor (Calendly's postMessage carries no identity).
let bookingContext = null;
export const setBookingContext = (ctx) => {
  bookingContext = { ...(ctx || {}), page: canUse() ? window.location.pathname : "" };
};
export const getBookingContext = () => bookingContext;
